import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Money } from '@principle-theorem/accounting';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
// Host-lib rationale: ng-payments owns the provider-backed capture panel;
// importing its result type here avoids duplicating capture orchestration in
// ng-appointment.
import { type AppointmentDepositDialogResult } from '@principle-theorem/ng-payments';
import {
  AccountCredit,
  Appointment,
  AppointmentCreator,
  Practice,
  TreatmentStep,
} from '@principle-theorem/principle-core';
import { AppointmentPermissions } from '@principle-theorem/principle-core/features';
import {
  DepositInvoiceClaimStatus,
  DepositStatus,
  IPractice,
  isDepositLineItem,
  isUnpaidDepositStatus,
  type IAccountCredit,
  type IAppointment,
  type ICancelDepositRequestForManualRecordRequest,
  type ICreateManualDepositRequestRequest,
  type IDepositLineItem,
  type IDepositMetadata,
  type IInvoice,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  httpsCallable,
  isObject,
  isSameRef,
  serialise,
  SerialisedData,
  snapshot,
  toTimestamp,
  undeletedQuery,
  where,
  type DocumentReference,
  type Timestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { type IAppointmentDepositCapturePresenter } from './appointment-deposit-capture-presenter';

interface IDepositAlreadyPaidError {
  readonly details: {
    readonly code: 'deposit-already-paid';
  };
}

const invoiceCreationRetryMessage =
  'Deposit invoice creation is already in progress. Please try again shortly.';
const invoiceAlreadyExistsMessage =
  'A deposit invoice already exists for this appointment. Refresh and try again.';
const interruptedCaptureMessage =
  'Deposit capture could not be finalised. Do not retry — contact support.';
const depositAlreadyPaidMessage = 'This deposit was already paid online';
const recordFailureMessage =
  'Unable to record the appointment deposit. Please try again.';
const forfeitureTrackingFailureMessage =
  'Deposit recorded, but automatic forfeiture could not be enabled for it. It can still be forfeited manually.';

export class AppointmentDepositReservationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AppointmentDepositReservationError';
  }
}

export class AppointmentDepositInvoiceClaimError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AppointmentDepositInvoiceClaimError';
  }
}

@Injectable()
export class RecordAppointmentDepositService {
  constructor(
    private _snackBar: MatSnackBar,
    private _organisation: OrganisationService
  ) {}

  /**
   * Records the deposit, resolving `true` only once it is persisted as paid.
   *
   * Every failure path here already reports itself, so a `false` means "nothing
   * was captured, and the operator has been told why". Callers use this rather
   * than re-reading the appointment, which races Firestore propagation.
   */
  async record(
    staleAppointment: WithRef<IAppointment>,
    presenter: IAppointmentDepositCapturePresenter
  ): Promise<boolean> {
    try {
      return await this._record(staleAppointment, presenter);
    } catch (error) {
      if (this._isDepositAlreadyPaidError(error)) {
        this._showDepositAlreadyPaid();
        return false;
      }
      if (error instanceof AppointmentDepositReservationError) {
        this._blockInterruptedCapture(staleAppointment, error);
        return false;
      }
      this._reportUnexpectedFailure(staleAppointment, error);
      return false;
    }
  }

  private async _record(
    staleAppointment: WithRef<IAppointment>,
    presenter: IAppointmentDepositCapturePresenter
  ): Promise<boolean> {
    const appointment = await Firestore.getDoc(staleAppointment.ref);
    const deposit = appointment.deposit;
    if (!deposit) {
      return false;
    }
    if (!isUnpaidDepositStatus(deposit.metadata.status)) {
      this._showDepositAlreadyPaid();
      return false;
    }

    if (deposit.invoiceRef && isUnpaidDepositStatus(deposit.metadata.status)) {
      return this._resumeInterruptedFinalisation(
        appointment,
        deposit.metadata,
        deposit.invoiceRef
      );
    }

    // A claim without an invoice pointer cannot distinguish an abandoned dialog
    // from a completed provider capture. The block is therefore INDEFINITE by
    // design: it outlives the five-minute claim lease and only support
    // intervention clears it. This is deliberate, not an oversight. The security
    // review lane assessed the alternative (releasing the claim once the lease
    // expires) and ruled it strictly worse, because an automatic release lets a
    // determined retry capture the patient's deposit a second time. A stuck
    // appointment is recoverable by a human; a double capture is not. Do not
    // "fix" this by adding a lease-bounded expiry.
    if (appointment.depositInvoiceCreationClaimedAt) {
      this._snackBar.open(interruptedCaptureMessage);
      return false;
    }

    const [patient, practice, canOverrideAmount] = await Promise.all([
      snapshot(Appointment.patient$(appointment)),
      Firestore.getDoc(appointment.practice.ref),
      snapshot(
        this._organisation.userHasPermission$(
          AppointmentPermissions.DepositOverride
        )
      ),
    ]);
    const brand = await Firestore.getDoc(Practice.brandRef(practice));

    let invoiceCreationClaimedAt: Timestamp | undefined;
    let result: AppointmentDepositDialogResult | undefined;

    try {
      result = await presenter.present({
        patient,
        practice,
        brand,
        depositContext: {
          appointmentRef: appointment.ref,
          amountCents: deposit.metadata.amountCents,
          canOverrideAmount,
          defaultDescription:
            deposit.metadata.description ??
            appointment.treatmentPlan.treatmentStep.name,
          practitioner: appointment.practitioner,
          treatmentCategoryRef: TreatmentStep.defaultDisplayRef(
            appointment.treatmentPlan.treatmentStep.display
          ),
          /**
           * Hands the claim back when capture proves it wrote nothing.
           *
           * Clearing the local handle as well as the persisted one is what
           * keeps the blocks below correct: every one of them asks whether
           * this attempt still holds a claim, so a released claim stops
           * reporting an interrupted capture that never happened.
           */
          abandonInvoiceClaim: async () => {
            if (!invoiceCreationClaimedAt) {
              return;
            }
            await AppointmentCreator.releaseDepositInvoiceCreationClaim(
              appointment.ref,
              invoiceCreationClaimedAt
            );
            invoiceCreationClaimedAt = undefined;
          },
          /**
           * Stands the online request down, then takes the claim.
           *
           * This cannot wait until the deposit has been taken. Cancelling
           * afterwards leaves a window where the patient pays the link while
           * the operator is taking the money in person, and the second payment
           * is discovered only once both have landed. Cancelling first means a
           * deposit already paid online is reported before anything is
           * captured, which is the one ordering that cannot double-charge.
           *
           * It runs here rather than before the method chooser so that merely
           * opening the panel and backing out no longer destroys the patient's
           * payment link — by this point the operator has chosen how to take
           * the money and pressed the button.
           */
          acquireInvoiceClaim: async () => {
            await snapshot(
              httpsCallable<
                SerialisedData<ICancelDepositRequestForManualRecordRequest>,
                void
              >('http-stripe-cancelDepositRequestForManualRecord')(
                serialise({
                  practiceRef: practice.ref,
                  appointmentRef: appointment.ref,
                })
              )
            );
            const claim = await AppointmentCreator.claimDepositInvoiceCreation(
              appointment.ref,
              deposit.metadata
            );
            switch (claim.status) {
              case DepositInvoiceClaimStatus.Leased:
                throw new AppointmentDepositInvoiceClaimError(
                  invoiceCreationRetryMessage
                );
              case DepositInvoiceClaimStatus.Exists:
                throw new AppointmentDepositInvoiceClaimError(
                  invoiceAlreadyExistsMessage
                );
              case DepositInvoiceClaimStatus.Claimed:
                invoiceCreationClaimedAt = claim.claimedAt;
                return;
              default: {
                const exhaustiveClaim: never = claim;
                void exhaustiveClaim;
                throw new AppointmentDepositInvoiceClaimError(
                  'Unexpected invoice claim result'
                );
              }
            }
          },
        },
      });
    } catch (error) {
      if (invoiceCreationClaimedAt) {
        this._blockInterruptedCapture(appointment, error);
        return false;
      }
      throw error;
    }

    if (!result) {
      if (invoiceCreationClaimedAt) {
        this._blockInterruptedCapture(
          appointment,
          new AppointmentDepositInvoiceClaimError(
            'Deposit capture returned without an invoice to link'
          )
        );
      }
      return false;
    }
    if ('status' in result) {
      if (invoiceCreationClaimedAt) {
        this._blockInterruptedCapture(appointment, result.error);
        return false;
      }
      // Standing the online request down is the first thing the claim gate
      // does, so its "already paid" answer now surfaces here rather than from
      // the outer catch. Reporting it as a generic failure would tell the
      // operator to retry a deposit the patient has already paid.
      if (this._isDepositAlreadyPaidError(result.error)) {
        this._showDepositAlreadyPaid();
        return false;
      }
      if (result.error instanceof AppointmentDepositInvoiceClaimError) {
        this._snackBar.open(result.error.message);
        return false;
      }
      // A claim error is an expected refusal and says so itself. Anything else
      // reaching here is an unexplained failure on the money path, and stays as
      // loud as it was when the outer catch reported it.
      this._reportUnexpectedFailure(appointment, result.error);
      return false;
    }
    if (!invoiceCreationClaimedAt) {
      throw new AppointmentDepositInvoiceClaimError(
        'Deposit invoice claim was not acquired before capture'
      );
    }

    const paidMetadata: IDepositMetadata = {
      ...deposit.metadata,
      status: DepositStatus.Paid,
      amountCents: result.amountCents,
      paidAt: toTimestamp(),
    };

    let linking: 'complete' | 'retry';
    try {
      linking = await AppointmentCreator.linkDepositInvoiceForManualRecord(
        appointment.ref,
        result.invoice.ref,
        invoiceCreationClaimedAt,
        paidMetadata
      );
    } catch (error) {
      this._blockInterruptedCapture(appointment, error);
      return false;
    }
    if (linking === 'retry') {
      this._blockInterruptedCapture(
        appointment,
        new AppointmentDepositInvoiceClaimError(
          'Captured deposit invoice could not be linked to its claim'
        )
      );
      return false;
    }

    await this._assertExactlyOneReservedCredit(
      appointment,
      patient,
      result.invoice
    );
    await this._createManualDepositRequest(appointment, practice);
    return true;
  }

  private async _resumeInterruptedFinalisation(
    appointment: WithRef<IAppointment>,
    depositMetadata: IDepositMetadata,
    invoiceRef: DocumentReference<IInvoice>
  ): Promise<boolean> {
    const [patient, practice, invoice] = await Promise.all([
      snapshot(Appointment.patient$(appointment)),
      Firestore.getDoc(appointment.practice.ref),
      Firestore.getDoc(invoiceRef),
    ]);
    const depositLineItem = await this._assertExactlyOneReservedCredit(
      appointment,
      patient,
      invoice
    );
    const finalisation = await AppointmentCreator.setDepositOnAppointment(
      appointment.ref,
      {
        ...depositMetadata,
        status: DepositStatus.Paid,
        amountCents: Money.toCents(depositLineItem.amount),
        paidAt: invoice.paidAt ?? depositMetadata.paidAt ?? toTimestamp(),
      },
      patient,
      practice
    );
    if (finalisation === 'retry') {
      this._snackBar.open(invoiceCreationRetryMessage);
      return false;
    }
    await this._createManualDepositRequest(appointment, practice);
    return true;
  }

  /**
   * Backfills the server-side deposit request that lets a manually recorded
   * appointment deposit participate in automatic forfeiture like an
   * online-paid one.
   *
   * By the time this runs the deposit itself is recorded and its metadata is
   * Paid, so a failure here only degrades automatic forfeiture. It is
   * reported as a non-blocking warning rather than failing the record flow —
   * staff can still forfeit the deposit manually.
   */
  private async _createManualDepositRequest(
    appointment: WithRef<IAppointment>,
    practice: WithRef<IPractice>
  ): Promise<void> {
    try {
      await snapshot(
        httpsCallable<SerialisedData<ICreateManualDepositRequestRequest>, void>(
          'http-stripe-createManualDepositRequest'
        )(
          serialise({
            practiceRef: practice.ref,
            appointmentRef: appointment.ref,
          })
        )
      );
    } catch (error) {
      // eslint-disable-next-line no-console -- the deposit is recorded; only automatic forfeiture is degraded, and this UI library has no logger dependency.
      console.error(
        `RecordAppointmentDepositService: failed to create the manual deposit request for ${appointment.ref.path}`,
        error
      );
      this._snackBar.open(forfeitureTrackingFailureMessage);
    }
  }

  private _blockInterruptedCapture(
    appointment: WithRef<IAppointment>,
    error: unknown
  ): void {
    // eslint-disable-next-line no-console -- an unlinked provider capture is a money-integrity incident and this UI library has no logger dependency.
    console.error(
      `[CRITICAL] RecordAppointmentDepositService: captured deposit could not be linked for ${appointment.ref.path}`,
      error
    );
    this._snackBar.open(interruptedCaptureMessage);
  }

  private _isDepositAlreadyPaidError(
    error: unknown
  ): error is IDepositAlreadyPaidError {
    return (
      isObject(error) &&
      isObject(error.details) &&
      error.details.code === 'deposit-already-paid'
    );
  }

  private _showDepositAlreadyPaid(): void {
    this._snackBar
      .open(depositAlreadyPaidMessage, 'Refresh')
      .onAction()
      .subscribe(() => window.location.reload());
  }

  private _reportUnexpectedFailure(
    appointment: WithRef<IAppointment>,
    error: unknown
  ): void {
    // eslint-disable-next-line no-console -- callers cannot remediate an unexpected money-path failure and this UI library has no logger dependency.
    console.error(
      `[CRITICAL] RecordAppointmentDepositService: failed to record deposit for ${appointment.ref.path}`,
      error
    );
    this._snackBar.open(recordFailureMessage);
  }

  private async _assertExactlyOneReservedCredit(
    appointment: WithRef<IAppointment>,
    patient: WithRef<IPatient>,
    invoice: WithRef<IInvoice>
  ): Promise<IDepositLineItem> {
    const depositLineItem = this._exactlyOneAppointmentDepositLineItem(
      appointment,
      invoice
    );
    const credits = await Firestore.getDocs(
      undeletedQuery(AccountCredit.col(patient)),
      where('invoice', '==', invoice.ref)
    );
    const matchingCredits = credits.filter(
      (credit) => credit.depositUid === depositLineItem.uid
    );

    if (matchingCredits.length !== 1) {
      throw new AppointmentDepositReservationError(
        `Expected exactly one account credit for invoice ${invoice.ref.path} and deposit ${depositLineItem.uid}; found ${matchingCredits.length}.`
      );
    }

    const matchingCredit = matchingCredits[0];
    // The client must stamp this reservation: Invoice.getAssociatedAppointment queries
    // top-level appointment.invoiceRef, which deposits never set. Capture synchronously
    // commits the server credit through add-transaction before the preceding link sets
    // deposit.invoiceRef, so changing that core lookup would still resolve undefined;
    // the committed credit is ready here without retrying or polling.
    await Firestore.patchDoc(matchingCredit.ref, {
      reservedFor: { appointmentRef: appointment.ref },
    });
    this._assertReservedForAppointment(
      {
        ...matchingCredit,
        reservedFor: {
          ...matchingCredit.reservedFor,
          appointmentRef: appointment.ref,
        },
      },
      appointment
    );
    return depositLineItem;
  }

  private _exactlyOneAppointmentDepositLineItem(
    appointment: WithRef<IAppointment>,
    invoice: WithRef<IInvoice>
  ): IDepositLineItem {
    const depositLineItems = invoice.items
      .filter(isDepositLineItem)
      .filter(
        (item) =>
          !!item.appointmentRef &&
          isSameRef(item.appointmentRef, appointment.ref)
      );
    if (depositLineItems.length !== 1) {
      throw new AppointmentDepositReservationError(
        `Expected exactly one appointment deposit line item on invoice ${invoice.ref.path}; found ${depositLineItems.length}.`
      );
    }
    return depositLineItems[0];
  }

  private _assertReservedForAppointment(
    credit: WithRef<IAccountCredit>,
    appointment: WithRef<IAppointment>
  ): void {
    if (
      !credit.reservedFor.appointmentRef ||
      !isSameRef(credit.reservedFor.appointmentRef, appointment.ref)
    ) {
      throw new AppointmentDepositReservationError(
        `Account credit ${credit.ref.path} is not reserved for appointment ${appointment.ref.path}.`
      );
    }
  }
}
